import React, { useCallback, useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AlertTriangle, Loader2, ShieldCheck, X } from 'lucide-react';

const s = (value) => (value == null ? '' : String(value));

export default function CpscDivisionAdultExceptions({ userProfile, tenantId } = {}) {
  const role = userProfile?.role;
  const isPlatformRole = role === 'platform_owner' || role === 'platform_admin';
  const isAdmin = isPlatformRole || role === 'admin';
  const activeTenantId = tenantId || userProfile?.tenantId;
  const tenantArg = useCallback((extra = {}) => (isPlatformRole ? { tenantId: activeTenantId, ...extra } : extra), [isPlatformRole, activeTenantId]);
  const [exceptions, setExceptions] = useState([]);
  const [styleNo, setStyleNo] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    const response = await httpsCallable(getFunctions(), 'listCpscDivisionAdultExceptions')(tenantArg());
    setExceptions(Array.isArray(response.data?.exceptions) ? response.data.exceptions : []);
  }, [tenantArg]);

  useEffect(() => {
    if (isAdmin) refresh().catch((e) => setError(e?.message || 'Could not load adult exceptions.'));
  }, [isAdmin, refresh]);

  const add = async () => {
    if (!s(styleNo).trim() || !s(reason).trim()) { setError('Style and reason are required.'); return; }
    setBusy(true); setError('');
    try {
      await httpsCallable(getFunctions(), 'setCpscDivisionAdultException')(tenantArg({ styleNo: s(styleNo).trim(), reason: s(reason).trim() }));
      setStyleNo(''); setReason(''); await refresh();
    } catch (e) { setError(e?.message || 'Could not record the adult exception.'); }
    finally { setBusy(false); }
  };

  const revoke = async (record) => {
    const why = window.prompt(`Why are you revoking the adult exception for ${record.styleNo}?`);
    if (!why || !why.trim()) return;
    setBusy(true); setError('');
    try {
      await httpsCallable(getFunctions(), 'removeCpscDivisionAdultException')(tenantArg({ styleNo: record.styleNo, reason: why.trim() }));
      await refresh();
    } catch (e) { setError(e?.message || 'Could not revoke the adult exception.'); }
    finally { setBusy(false); }
  };

  if (!isAdmin) return null;

  return (
    <section className="border border-carbon-border bg-carbon-white p-3 space-y-2" aria-label="Division 15 adult exceptions">
      <div className="flex items-start gap-2">
        <ShieldCheck size={16} className="text-carbon-blue-60 mt-0.5" />
        <div>
          <h4 className="text-sm font-semibold">Division 15 adult exceptions</h4>
          <p className="text-xs text-carbon-gray-70">Division 15 defaults to children&apos;s. Record an exception only for a genuinely adult style; the server verifies current Division 15 and adult-apparel evidence.</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-[10rem_1fr_auto] gap-2">
        <input className="border border-carbon-border px-2 py-1.5 text-sm" value={styleNo} onChange={(e) => setStyleNo(e.target.value)} placeholder="Style" aria-label="Adult exception style" />
        <input className="border border-carbon-border px-2 py-1.5 text-sm" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason this Division 15 style is adult" aria-label="Adult exception reason" maxLength={500} />
        <button type="button" onClick={add} disabled={busy} className="inline-flex items-center justify-center gap-1 bg-carbon-blue-60 text-white px-3 py-1.5 text-sm disabled:opacity-50">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />} Record exception
        </button>
      </div>
      {error && <div className="text-xs text-carbon-red-60 flex items-start gap-1"><AlertTriangle size={12} className="mt-0.5" /> {error}</div>}
      {exceptions.length > 0 && <div className="space-y-1">
        {exceptions.map((record) => <div key={record.styleNo} className="flex items-center gap-2 text-xs bg-carbon-gray-10 border border-carbon-border px-2 py-1.5">
          <span className="font-plex-mono font-semibold">{record.styleNo}</span>
          <span className="text-carbon-gray-70 flex-1">{record.reason}</span>
          <button type="button" onClick={() => revoke(record)} disabled={busy} className="inline-flex items-center gap-1 text-carbon-red-60" aria-label={`Revoke adult exception ${record.styleNo}`}><X size={12} /> Revoke</button>
        </div>)}
      </div>}
    </section>
  );
}
